/**
 * VTT Analysis Types
 * Types for analyzing WebVTT caption files for gaps and issues
 */

/** Threshold for flagging long caption lines (chars) */
export const VTT_LONG_LINE_THRESHOLD = 100;

/** A single VTT caption segment with timestamps */
export interface VttSegment {
  start: number;  // seconds
  end: number;    // seconds
  text: string;
}

/** A gap between caption segments */
export interface VttGap {
  /** End timestamp of the segment before the gap (seconds) */
  afterEnd: number;
  /** Start timestamp of the segment after the gap (seconds) */
  beforeStart: number;
  /** Duration of the gap in seconds */
  gapSeconds: number;
}

/** A run of consecutive identical segments */
export interface VttConsecutiveRun {
  /** The repeated text content */
  text: string;
  /** Number of consecutive occurrences */
  count: number;
  /** Start timestamp of first occurrence (seconds) */
  startTime: number;
  /** End timestamp of last occurrence (seconds) */
  endTime: number;
}

/** Statistics about marker segments like [inaudible], [music], etc. */
export interface VttMarkerStats {
  /** Total marker segments found */
  totalMarkers: number;
  /** Percentage of segments that are markers */
  markerPercentage: number;
  /** Breakdown by marker type */
  byType: Record<string, number>;
}

/** A phrase that appears multiple times in the VTT */
export interface VttRepeatedPhrase {
  /** The phrase text (normalized) */
  phrase: string;
  /** Number of occurrences */
  count: number;
}

/** Repetition analysis results */
export interface VttRepetitionAnalysis {
  /** Total consecutive identical segment pairs found */
  consecutiveDuplicates: number;
  /** Longest run of identical segments */
  longestRun: number;
  /** Details of runs with 2+ consecutive identical segments */
  runs: VttConsecutiveRun[];
  /** Marker statistics */
  markers: VttMarkerStats;
  /** Top repeated phrases (non-marker text appearing 3+ times) */
  topRepeatedPhrases: VttRepeatedPhrase[];
}

/** A caption segment flagged for being too long */
export interface VttLongLine {
  /** Start timestamp (seconds) */
  start: number;
  /** Character count */
  charCount: number;
  /** The text (may be truncated for display) */
  text: string;
}

/** Quality analysis results */
export interface VttQualityAnalysis {
  /** Average characters per segment */
  avgCharsPerSegment: number;
  /** Segments exceeding VTT_LONG_LINE_THRESHOLD */
  longLines: VttLongLine[];
}

// ============================================================================
// Hallucination Detection Types
// ============================================================================

/** Threshold for flagging repeated words in a single segment */
export const VTT_WORD_REPETITION_THRESHOLD = 10;

/** A segment with repeated word hallucination (e.g., "uh, uh, uh...") */
export interface VttWordRepetition {
  /** Start timestamp (seconds) */
  start: number;
  /** The repeated word */
  word: string;
  /** Number of occurrences in the segment */
  count: number;
  /** The full text (truncated for display) */
  text: string;
}

/** A duplicate long text appearing in non-adjacent segments */
export interface VttDuplicateText {
  /** The duplicated text (truncated for display) */
  text: string;
  /** Timestamps where it appears (seconds) */
  timestamps: number[];
  /** Number of occurrences */
  count: number;
}

/** A detected common Whisper hallucination phrase */
export interface VttHallucinationPhrase {
  /** Start timestamp (seconds) */
  start: number;
  /** The detected phrase */
  phrase: string;
  /** Type of hallucination (e.g., "outro", "engagement", "filler") */
  type: string;
}

/** Hallucination analysis results */
export interface VttHallucinationAnalysis {
  /** Segments with >10 repeated words (like "uh, uh, uh...") */
  wordRepetitions: VttWordRepetition[];
  /** Long text (>30 chars) appearing in multiple non-adjacent segments */
  duplicateTexts: VttDuplicateText[];
  /** Known Whisper hallucination phrases detected */
  hallucinationPhrases: VttHallucinationPhrase[];
  /** Overall hallucination severity score (0-100) */
  score: number;
}

/** Full analysis result from VTT analyzer */
export interface VttAnalysisResult {
  /** The URL that was analyzed */
  url: string;
  /** Whether the analysis completed successfully */
  success: boolean;
  /** Error message if success is false */
  error?: string;

  // Summary stats
  /** Total number of caption segments found */
  totalSegments: number;
  /** Timestamp of first caption (seconds) */
  firstTimestamp: number;
  /** Timestamp of last caption end (seconds) */
  lastTimestamp: number;
  /** Total duration from first to last caption (seconds) */
  totalDurationSeconds: number;

  // Gap analysis
  /** List of minute markers with zero caption coverage */
  minutesWithNoCoverage: number[];
  /** Gaps > 5 seconds - noticeable but may be intentional silence */
  significantGaps: VttGap[];
  /** Gaps > 30 seconds - likely missing caption segments */
  largeGaps: VttGap[];

  // Repetition analysis
  /** Repetition metrics (consecutive duplicates, markers, repeated phrases) */
  repetition?: VttRepetitionAnalysis;

  // Quality analysis
  /** Text quality metrics (line length, etc.) */
  quality?: VttQualityAnalysis;

  // Hallucination detection
  /** Detected hallucination patterns (Whisper artifacts) */
  hallucinations?: VttHallucinationAnalysis;

  // Event context (populated when VTT URL matches a known media asset)
  /** Event info for reverse lookup (link back to event detail / player) */
  eventInfo?: {
    eventID: string;
    clientID: string;
    eventTitle: string | null;
  } | null;
}

/** Request body for VTT analysis endpoint */
export interface VttAnalyzeRequest {
  url: string;
}
